<template>
  <el-skeleton :loading="skeletonLoadingState" animated>
    <template #template>
      <el-skeleton-item />
    </template>
    <template #default>
      <div v-if="currentTokenInfoDataInjected?.isSellable" class="tw:group stats-section rewards">
        <div class="tw:group-hover:opacity-100 tw:opacity-0 stats-section-gradient" />

        <div class="stats-section-title">
          {{ store.currentToken === Tokens.pearl ? $t('appStatisticRewardsEarned') : $t('appStatisticProduced') }}
        </div>

        <DashboardTokenInfoBlock
          v-if="store.currentToken === Tokens.pearl || store.currentToken === Tokens.sand"
          :key="`rewards-${currentToken}`"
          :main-token-value="BigNumber(primaryTokenCanClaimInjected || 0).toFormat(2)"
        />
        <DashboardTokenInfoBlock
          v-else
          :key="`rewards-${currentToken}`"
          :extra-token-value="BigNumber(secondaryTokenCanClaimInjected || 0).toFormat(2)"
          :main-token-value="BigNumber(primaryTokenCanClaimInjected || 0).toFormat(2)"
        />

        <ClientOnly>
          <el-tooltip
            :disabled="
              (store.currentToken === Tokens.pearl && Number(earnedBalanceInjected) > 0) ||
              (store.currentToken !== Tokens.pearl &&
                BigNumber(formatEther(minedBalanceInjected?.[0] || BigInt(0))).isGreaterThan(0))
            "
            placement="top"
          >
            <template #content> {{ $t('appStatisticNothingToClaimYet') }} </template>
            <button
              :disabled="
                (store.currentToken === Tokens.pearl && Number(earnedBalanceInjected) === 0) ||
                (store.currentToken !== Tokens.pearl &&
                  BigNumber(formatEther(minedBalanceInjected?.[0] || BigInt(0))).isZero()) ||
                isLoadingClaim
              "
              :loading="isLoadingClaim"
              class="w-button btn-primary small custom-btn-claim"
              @click="claimRewardHandler"
            >
              <div class="btn-claim-wrapper">
                {{ $t('appStatisticClaim') }}
                <div class="claim-icon"></div>
              </div>
            </button>
          </el-tooltip>
        </ClientOnly>

        <div
          :class="{
            'mobile-centered': $device.isMobile
          }"
          class="available-for-producting"
        >
          <div v-if="store.currentToken !== Tokens.pearl" class="stats-section-title">
            {{ $t('appStatisticAvailableForProducting') }}
          </div>

          <DashboardTokenInfoBlock
            v-if="store.currentToken !== Tokens.pearl"
            :key="`available-${currentToken}`"
            :extra-token-value="BigNumber(secondaryToken).toFormat(2)"
            :main-token-value="BigNumber(primaryToken).toFormat(2)"
            columned
          />
        </div>
      </div>
    </template>
  </el-skeleton>
</template>

<script lang="ts" setup>
import { BigNumber } from 'bignumber.js';
import { type BigNumberish, formatEther } from 'ethers';
import type { ITokenModel } from '~/types/apiService';
import { Tokens } from '~/utils/constants';

const store = useMainStore();
const { currentToken } = storeToRefs(store);
const { claim, isLoading: isLoadingClaim } = useClaim();
const { isLoading: isLoadingAvailableForProducing, primaryToken, secondaryToken } = useAvailableForProducing();
const { isLoading: isLoadingTotalMinedAmount } = useTotalMined();
const { isLoading: isLoadingMinedBalance } = useMinedBalance();
const { isLoading: isLoadingUpdateDataStatistics } = useDashboard();

const minedBalanceInjected = inject<Ref<BigNumberish[] | null>>('minedBalance');
const updateDataStatisticsInjected = inject<(forceRefresh?: boolean) => Promise<void>>('updateDataStatistics');
const getTotalMinedAmountInjected = inject<() => Promise<void>>('getTotalMinedAmount');
const currentTokenInfoDataInjected = inject<Ref<ITokenModel>>('currentTokenInfoData');
const earnedBalanceInjected = inject<Ref<string>>('earnedBalance');

const primaryTokenCanClaimInjected = inject<Ref<BigNumber>>('primaryTokenCanClaim');
const secondaryTokenCanClaimInjected = inject<Ref<BigNumber>>('secondaryTokenCanClaim');

const skeletonLoadingState = computed(() => {
  const hasNoPrimaryTokenCanClaim =
    !primaryTokenCanClaimInjected?.value ||
    primaryTokenCanClaimInjected.value.toString() === '' ||
    primaryTokenCanClaimInjected.value.isZero();
  const hasNoPrimaryToken = primaryToken.value === 0;
  const hasNoSecondaryToken = secondaryToken.value === 0;

  const hasNoData = hasNoPrimaryTokenCanClaim && hasNoPrimaryToken && hasNoSecondaryToken;
  const isAnyLoading =
    isLoadingMinedBalance.value ||
    isLoadingUpdateDataStatistics.value ||
    isLoadingAvailableForProducing.value ||
    (isLoadingTotalMinedAmount?.value && currentToken.value !== Tokens.pearl);

  return hasNoData && isAnyLoading;
});

const getMinedBalanceInjected = inject<() => Promise<void>>('getMinedBalance');

const claimRewardHandler = async () => {
  await claim();

  await getMinedBalanceInjected?.();
  await getTotalMinedAmountInjected?.();
  await updateDataStatisticsInjected?.(true);
};
</script>

<style lang="scss" scoped>
.stats-section-gradient {
  z-index: 0;
  background-image: radial-gradient(circle at 0 100%, rgba(140, 253, 159, 0.2), transparent 50%);
  transition: opacity 0.3s;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
}

.stats-section {
  background-image: radial-gradient(circle at 100% 100%, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0) 50%);
  border: 2px solid #133442;
  border-radius: 12px;
  flex-direction: column;
  flex: 1;
  align-items: flex-start;
  padding: 12px 16px 16px;
  font-size: 24px;
  transition: all 0.3s;
  display: flex;
  position: relative;
  overflow: hidden;
  min-height: 370px;
  min-width: 32.35%;

  @media screen and (max-width: 991px) {
    font-size: 16px;
  }

  @media screen and (max-width: 560px) {
    width: 100%;
    flex: none;
    align-items: center;
    min-height: unset;
  }
}

.stats-section-title {
  font-size: 24px;
  font-weight: 400;

  @media screen and (max-width: 560px) {
    text-align: center;
  }

  @media screen and (max-width: 991px) {
    font-size: 16px;
  }
}

.stats-section.rewards {
  background-image: radial-gradient(circle at 0 100%, rgba(10, 10, 10, 0.6), rgba(0, 0, 0, 0) 50%);

  @media screen and (max-width: 560px) {
    > button {
      margin-top: 8px;
    }
  }
}

.btn-claim-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  grid-column-gap: 7px;
}

.custom-btn-claim {
  cursor: pointer;
  padding: 14px 24px;

  .claim-icon {
    background-image: url('~/public/img/icons/login-arrow.svg');
    background-position: center;
    background-repeat: no-repeat;
    background-size: contain;
    height: 12px;
    width: 15px;
    transition: background-image 0.3s ease;
  }

  &:not(:disabled):hover {
    .claim-icon {
      background-image: url('~/public/img/icons/login-arrow-hover.svg');
    }
  }
}

.available-for-producting {
  margin-top: 15px;
}

.mobile-centered {
  display: flex;
  flex-direction: column;
  align-items: center;
}
</style>
